import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  FiImage, 
  FiX, 
  FiCheck,
  FiSearch,
  FiUpload,
  FiGrid,
  FiList
} from 'react-icons/fi'
import { useMedia } from '../../hooks/useMedia'
import { MediaFile, MediaViewMode } from '../../types/media'
import ImageUpload from './ImageUpload'
import VideoUpload from './VideoUpload'

interface MediaPickerProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (mediaUrl: string) => void
  selectedUrl?: string
  mediaType?: 'image' | 'video' | 'all'
}

const MediaPicker: React.FC<MediaPickerProps> = ({
  isOpen,
  onClose,
  onSelect,
  selectedUrl,
  mediaType = 'image'
}) => {
  const { mediaFiles, loading } = useMedia()
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<MediaViewMode>('grid')
  const [showUpload, setShowUpload] = useState(false)

  const filteredFiles = mediaFiles
    .filter(file => {
      if (mediaType === 'all') return true
      return file.type === mediaType
    })
    .filter(file => 
      file.filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
      file.originalName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      file.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    )

  const handleSelect = (file: MediaFile) => {
    onSelect(file.url)
    onClose()
  }

  const handleUploadComplete = (urls: string[]) => {
    if (urls.length > 0) {
      onSelect(urls[0]) // Select the first uploaded image
      setShowUpload(false)
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-neutral-200">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FiImage className="text-primary" size={20} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-neutral-800">Select Image</h2>
                <p className="text-sm text-neutral-600">Choose from uploaded images or upload new ones</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {/* Upload Toggle */}
              <button
                onClick={() => setShowUpload(!showUpload)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-200 ${
                  showUpload
                    ? 'bg-primary text-white'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
              >
                <FiUpload size={16} />
                <span>{showUpload ? 'Hide Upload' : 'Upload New'}</span>
              </button>

              {/* Close Button */}
              <button
                onClick={onClose}
                className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-xl transition-colors"
              >
                <FiX size={20} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {showUpload ? (
              /* Upload Section */
              <div className="p-6">
                {mediaType === 'video' ? (
                  <VideoUpload
                    onUploadComplete={(videoUrl) => handleUploadComplete([videoUrl])}
                    maxFiles={1}
                  />
                ) : (
                  <ImageUpload
                    onUploadComplete={handleUploadComplete}
                    maxFiles={5}
                  />
                )}
              </div>
            ) : (
              /* Media Library */
              <div className="h-full flex flex-col">
                {/* Search and Controls */}
                <div className="p-6 border-b border-neutral-200">
                  <div className="flex items-center space-x-4">
                    {/* Search */}
                    <div className="flex-1">
                      <div className="relative">
                        <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" size={20} />
                        <input
                          type="text"
                          placeholder="Search images..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                        />
                      </div>
                    </div>

                    {/* View Mode Toggle */}
                    <div className="flex items-center bg-neutral-100 rounded-xl p-1">
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 ${
                          viewMode === 'grid'
                            ? 'bg-white text-primary shadow-sm'
                            : 'text-neutral-600 hover:text-neutral-800'
                        }`}
                      >
                        <FiGrid size={16} />
                        <span className="text-sm">Grid</span>
                      </button>
                      <button
                        onClick={() => setViewMode('list')}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 ${
                          viewMode === 'list'
                            ? 'bg-white text-primary shadow-sm'
                            : 'text-neutral-600 hover:text-neutral-800'
                        }`}
                      >
                        <FiList size={16} />
                        <span className="text-sm">List</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Media Grid */}
                <div className="flex-1 overflow-y-auto p-6">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="loading-spinner"></div>
                      <span className="ml-2 text-neutral-600">Loading images...</span>
                    </div>
                  ) : filteredFiles.length === 0 ? (
                    <div className="text-center py-12">
                      <FiImage size={48} className="text-neutral-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                        {searchQuery ? 'No images found' : 'No images uploaded yet'}
                      </h3>
                      <p className="text-neutral-600 mb-6">
                        {searchQuery 
                          ? 'Try adjusting your search terms'
                          : 'Upload your first images to get started'
                        }
                      </p>
                      {!searchQuery && (
                        <button
                          onClick={() => setShowUpload(true)}
                          className="btn-primary inline-flex items-center space-x-2"
                        >
                          <FiUpload size={20} />
                          <span>Upload {mediaType === 'video' ? 'Videos' : 'Images'}</span>
                        </button>
                      )}
                    </div>
                  ) : viewMode === 'grid' ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {filteredFiles.map((file, index) => (
                        <motion.div
                          key={file.id}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          onClick={() => handleSelect(file)}
                          className={`group relative bg-neutral-50 rounded-xl overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-200 ${
                            selectedUrl === file.url ? 'ring-2 ring-primary' : ''
                          }`}
                        >
                          {/* Selection Indicator */}
                          {selectedUrl === file.url && (
                            <div className="absolute top-2 right-2 z-10">
                              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                                <FiCheck className="text-white" size={14} />
                              </div>
                            </div>
                          )}

                          {/* Image */}
                          <div className="aspect-square">
                            <img
                              src={file.url}
                              alt={file.altText || file.originalName}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            />
                          </div>

                          {/* Overlay */}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                                <FiCheck className="text-primary" size={16} />
                              </div>
                            </div>
                          </div>

                          {/* File Info */}
                          <div className="p-3 bg-white">
                            <p className="text-sm font-medium text-neutral-800 truncate">
                              {file.originalName}
                            </p>
                            <p className="text-xs text-neutral-500">
                              {file.dimensions && `${file.dimensions.width}×${file.dimensions.height}`}
                            </p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    /* List View */
                    <div className="space-y-2">
                      {filteredFiles.map((file, index) => (
                        <motion.div
                          key={file.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.02 }}
                          onClick={() => handleSelect(file)}
                          className={`flex items-center space-x-4 p-4 rounded-xl cursor-pointer hover:bg-neutral-50 transition-colors duration-200 ${
                            selectedUrl === file.url ? 'bg-primary/5 border border-primary/20' : 'border border-transparent'
                          }`}
                        >
                          {/* Thumbnail */}
                          <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                            <img
                              src={file.url}
                              alt={file.altText || file.originalName}
                              className="w-full h-full object-cover"
                            />
                          </div>

                          {/* File Info */}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-neutral-800 truncate">
                              {file.originalName}
                            </p>
                            <p className="text-sm text-neutral-600">
                              {file.dimensions && `${file.dimensions.width}×${file.dimensions.height}`}
                              {file.dimensions && ' • '}
                              {Math.round(file.size / 1024)}KB
                            </p>
                          </div>

                          {/* Selection Indicator */}
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                            selectedUrl === file.url
                              ? 'bg-primary border-primary'
                              : 'border-neutral-300'
                          }`}>
                            {selectedUrl === file.url && (
                              <FiCheck className="text-white" size={14} />
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-neutral-200 bg-neutral-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-neutral-600">
                {filteredFiles.length} image{filteredFiles.length !== 1 ? 's' : ''} available
              </div>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-neutral-600 hover:text-neutral-800 transition-colors"
                >
                  Cancel
                </button>
                
                {selectedUrl && (
                  <button
                    onClick={() => {
                      onSelect(selectedUrl)
                      onClose()
                    }}
                    className="btn-primary flex items-center space-x-2"
                  >
                    <FiCheck size={16} />
                    <span>Use Selected Image</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default MediaPicker
